/**
 * Frontend logger utility for controlled logging across the application.
 *
 * @example
 * import { logger } from './utils/logger'
 *
 * logger.error('Error message', errorObject)
 * logger.warn('Warning message', metadata)
 * logger.info('Info message')
 * logger.debug('Debug message', { key: 'value' })
 *
 * Log levels (from most to least restrictive):
 * - silent: No logs
 * - error: Only errors (default in production)
 * - warn: Errors and warnings
 * - info: Errors, warnings, and info
 * - debug: All logs (default in development)
 *
 * Control log level via environment variable:
 * - Set LOG_LEVEL=debug|info|warn|error|silent in .env file
 * - If LOG_LEVEL is not set, defaults to 'error' in production, 'debug' in development
 *
 * Logs are automatically filtered based on the current log level.
 * Timestamps are included in all log messages.
 */

import { getEnvVar } from './env'

type LogLevel = 'error' | 'warn' | 'info' | 'debug' | 'silent'

const LOG_LEVELS: Record<LogLevel, number> = {
  silent: 0,
  error: 1,
  warn: 2,
  info: 3,
  debug: 4,
}

const getLogLevel = (): LogLevel => {
  const level = getEnvVar({ key: 'VITE_LOG_LEVEL' }) ?? ''
  return (
    LOG_LEVELS[level as LogLevel] !== undefined ? level : 'error'
  ) as LogLevel
}

/** Resolved once at module load; runtime config may not be set yet. shouldLog re-reads so runtime logLevel is used after `main()` sets client config runtime. */
const shouldLog = ({ level }: { level: LogLevel }): boolean => {
  const currentLogLevel = getLogLevel()
  const currentLevelValue = LOG_LEVELS[currentLogLevel]
  return LOG_LEVELS[level] <= currentLevelValue
}

const formatMessage = ({
  level,
  message,
  metadata,
}: {
  level: LogLevel
  message: string
  metadata: unknown[]
}): void => {
  if (!shouldLog({ level })) {
    return
  }

  const timestamp = new Date().toISOString()
  const hasMetadata = metadata.length > 0

  const logMethod =
    level === 'error'
      ? console.error
      : level === 'warn'
        ? console.warn
        : console.log
  const prefix = `[${timestamp}] ${level.toUpperCase()}:`

  if (hasMetadata) {
    logMethod(`${prefix} ${message}`, ...metadata)
  } else {
    logMethod(`${prefix} ${message}`)
  }
}

export const logger = {
  error: (message: string, ...metadata: unknown[]) => {
    formatMessage({ level: 'error', message, metadata })
  },
  warn: (message: string, ...metadata: unknown[]) => {
    formatMessage({ level: 'warn', message, metadata })
  },
  info: (message: string, ...metadata: unknown[]) => {
    formatMessage({ level: 'info', message, metadata })
  },
  debug: (message: string, ...metadata: unknown[]) => {
    formatMessage({ level: 'debug', message, metadata })
  },
}
